
import circlesAPI from '~/api/circles';
import accountAPI from '~/api/account';

export default {
  name: 'NewAccountSelector',
  props: {
    types: {
      type: String,
      default: 'client',
    },
    label: {
      type: String,
      default: 'Select contacts:',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    allowCircles: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
    roles: {
      type: Array,
      required: false,
      default: () => [],
    },
    client: {
      type: Boolean,
      required: false,
      default: false,
    },
    employee: {
      type: Boolean,
      required: false,
      default: false,
    },
    external: {
      type: Boolean,
      required: false,
      default: false,
    },
    background: {
      type: String,
      default: null,
    },
    maximum: {
      type: Number,
      required: false,
      default: 0,
    },
    excludeUuids: {
      type: Array | String,
      default: () => [],
    },
    childAccounts: {
      type: String,
      default: 'include',
      validator: function (value) {
        return ['include', 'exclude', 'only'].includes(value);
      },
    },
    includeExtraFields: {
      type: String | null,
      required: false,
      default: null,
    },
    includeExtraFilters: {
      type: Object | null,
      required: false,
      default: () => {},
    },
  },
  data: () => {
    return {
      isSearching: true,
      fetchingSingleAccount: false,
      adding: null,
      nowAdding: null,
      nowAddingCircle: [],
      fullAccounts: [],
    };
  },

  computed: {
    accountList() {
      return this.fullAccounts.filter((item) => item && item.uuid).map((item) => item.uuid);
    },

    canAddMore() {
      if (this.multiple) {
        return true;
      } else {
        return this.fullAccounts.length === 0;
      }
    },

    maximumAllowed: {
      get() {
        if (this.maximum > 0) {
          return `Max ${this.maximum} accounts`;
        }
        return null;
      },
    },
  },

  methods: {
    confirmNow() {
      if (this.adding === 'client') {
        if (this.multiple === false) {
          this.nowAdding = [this.nowAdding];
        }
        if (this.nowAdding.length === 0) {
          this.$nuxt.$emit('notification', 'Please select at least one account');
          return;
        }
        this.nowAdding.forEach((item) => {
          if (!this.accountList.includes(item.uuid)) {
            this.fullAccounts.push(item);
          }
        });
      }
      if (this.adding === 'circle') {
        let circles = this.nowAddingCircle.map((item) => item.uuid);
        circlesAPI
          .all({
            params: {
              include: 'accounts',
              'filter[uuid]': circles,
              per_page: 100,
            },
          })
          .then((rsp) => {
            rsp.data.data.forEach((item) => {
              item.accounts.forEach((account) => {
                if (account && account.uuid && !this.accountList.includes(account.uuid)) {
                  this.fullAccounts.push(account);
                }
              });
            });
          })
          .catch((err) => {
            this.$nuxt.$emit('notification', 'Failed to add accounts from circle');
          });
      }
      this.resetState();
    },
    resetState() {
      this.adding = null;
      this.isSearching = false;
      this.nowAdding = null;
      this.nowAddingCircle = null;
    },
    fullReset() {
      this.resetState();
      this.fullAccounts = [];
    },
    removeMe(account) {
      this.fullAccounts = this.fullAccounts.filter((item) => item.uuid !== account.uuid);
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal && newVal.length > 0) {
          let uuids = newVal.map((item) => item.uuid);
          let missing = [];
          if (this.fullAccounts.length === 0) {
            missing = uuids;
          } else {
            missing = this.accountList.filter((item) => {
              return !uuids.includes(item);
            });
          }
          if (missing.length > 0) {
            if (newVal.length === 1 && !this.multiple) {
              this.fetchingSingleAccount = true;
            }

            const params = {
              per_page: 100,
              'filter[uuid]': missing.join(','),
              include: 'assigned_adviser',
            };

            if (this.includeExtraFields) {
              params.include = this.includeExtraFields;
            }

            if (this.includeExtraFilters && Object.keys(this.includeExtraFilters).length > 0) {
              Object.keys(this.includeExtraFilters).forEach((key) => {
                params[`filter[${key}]`] = this.includeExtraFilters[key];
              });
            }

            if (this.excludeUuids.length > 0) {
              params['filter[exclude_uuid]'] = Array.isArray(this.excludeUuids)
                ? this.excludeUuids.join(',')
                : this.excludeUuids;
            }

            if (this.childAccounts !== 'include') {
              params['filter[is_child]'] = this.childAccounts;
            }

            accountAPI
              .getAccounts({ params: params })
              .then((rsp) => {
                this.fullAccounts = rsp.data.data;
              })
              .catch((err) => {
                this.$nuxt.$emit('notification', 'Failed to add accounts');
              })
              .finally(() => {
                this.fetchingSingleAccount = false;
              });
          }
        }

        // if the array is reset from the parent
        if (newVal?.length === 0 && oldVal?.length > 0) {
          this.fullAccounts = [];
        }
      },
    },

    fullAccounts: {
      deep: true,
      handler(newVal) {
        this.$emit('input', newVal);
      },
    },

    adding: {
      handler(newVal) {
        this.$emit('adding', newVal);
      },
    },
  },
};
